import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Button,
  Card,
  Divider,
  Input,
  List,
  Radio,
  Space,
  Spin,
  Steps,
  Typography,
} from "antd";
import { message } from "antd";
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import axiosInstance from "../redux/apiAxios";
import { fetchOrganzation } from "../redux/organizationSlice";
import GoogleAdsLogo from "../components/icons/GoogleAdsLogo";

const { Title, Text } = Typography;

const STEP_SIGN_IN = 0;
const STEP_ACCOUNT = 1;
const STEP_CONVERSION = 2;

const GoogleAdsOAuthCallback = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentOrganizationId = useSelector(
    (state) => state?.auth?.currentOrganizationId
  );

  const [flowStep, setFlowStep] = useState("connecting");
  const [statusLine, setStatusLine] = useState("Signing in with Google…");
  const [stepsCurrent, setStepsCurrent] = useState(STEP_SIGN_IN);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [customerIdInput, setCustomerIdInput] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const goToOrgSettings = useCallback(() => {
    navigate("/orgSettings", { replace: true });
  }, [navigate]);

  const linkCustomerAndFinish = async (customer_id) => {
    const normalized = String(customer_id || "").replace(/\D/g, "");
    if (!normalized) {
      message.error("Enter a valid customer ID");
      return;
    }
    try {
      setActionLoading(true);
      await axiosInstance.post("/google-ads/link-customer", {
        customer_id: normalized,
        login_customer_id: "",
      });
      const auto2 = await axiosInstance.post("/google-ads/setup/auto");

      if (auto2.data?.ok && auto2.data?.step === "complete") {
        setFlowStep("success");
        setStepsCurrent(STEP_CONVERSION);
        setStatusLine("Google Ads is ready");
        message.success(auto2.data?.message || "Google Ads setup complete.");
        if (currentOrganizationId) {
          await dispatch(fetchOrganzation(currentOrganizationId));
        }
        setTimeout(() => goToOrgSettings(), 2200);
        return;
      }

      if (auto2.data?.step === "need_choice") {
        const list = auto2.data.customers || [];
        setCustomers(list);
        setSelectedCustomerId(list?.[0]?.customerId || "");
        setFlowStep("choose");
        setStepsCurrent(STEP_ACCOUNT);
        setStatusLine("Choose the Google Ads account to use");
        message.info("Please choose which account to use.");
        return;
      }

      if (auto2.data?.step === "need_customer") {
        setFlowStep("input");
        setCustomers([]);
        setStepsCurrent(STEP_ACCOUNT);
        setStatusLine(
          auto2.data?.message || "Enter your Google Ads customer ID (10 digits)"
        );
        message.warning(auto2.data?.message || "Customer ID required.");
        return;
      }

      message.warning(auto2.data?.message || "Google Ads setup not finished yet.");
    } catch (e) {
      setErrorMessage(e?.response?.data?.message || e.message || "Google Ads setup failed");
      setFlowStep("error");
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const err = params.get("error");
    const errDesc = params.get("error_description");

    if (err) {
      setErrorMessage(errDesc || err || "Google authorization failed");
      setFlowStep("error");
      return;
    }
    if (!code || !state) {
      setErrorMessage("Missing OAuth parameters. Return to Integration Keys and try again.");
      setFlowStep("error");
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setFlowStep("connecting");
        setStepsCurrent(STEP_SIGN_IN);
        setStatusLine("Connecting your Google Ads account…");
        await axiosInstance.post("/google-ads/oauth/exchange", { code, state });
        if (cancelled) return;
        if (currentOrganizationId) {
          await dispatch(fetchOrganzation(currentOrganizationId));
        }
        setStepsCurrent(STEP_ACCOUNT);
        setStatusLine("Finishing setup — conversion action…");
        const auto = await axiosInstance.post("/google-ads/setup/auto");
        if (cancelled) return;

        if (auto.data?.ok && auto.data?.step === "complete") {
          setFlowStep("success");
          setStepsCurrent(STEP_CONVERSION);
          setStatusLine("Google Ads is ready");
          message.success(
            auto.data?.message ||
              "Google Ads is ready. Bookings will send conversions when gclid (or wbraid/gbraid) is present."
          );
          if (currentOrganizationId) {
            await dispatch(fetchOrganzation(currentOrganizationId));
          }
          setTimeout(() => goToOrgSettings(), 2200);
          return;
        }

        if (auto.data?.step === "need_choice") {
          const list = auto.data.customers || [];
          setCustomers(list);
          setSelectedCustomerId(list?.[0]?.customerId || "");
          setCustomerIdInput("");
          setFlowStep("choose");
          setStepsCurrent(STEP_ACCOUNT);
          setStatusLine("Choose the Google Ads account for this organization");
          return;
        }

        if (auto.data?.step === "need_customer") {
          setCustomerIdInput("");
          setCustomers([]);
          setFlowStep("input");
          setStepsCurrent(STEP_ACCOUNT);
          setStatusLine(
            auto.data?.message || "Enter your Google Ads customer ID (10 digits)"
          );
          return;
        }

        if (currentOrganizationId) {
          await dispatch(fetchOrganzation(currentOrganizationId));
        }
        goToOrgSettings();
      } catch (e) {
        if (!cancelled) {
          setErrorMessage(
            e?.response?.data?.message || e.message || "OAuth exchange failed"
          );
          setFlowStep("error");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentOrganizationId, dispatch, goToOrgSettings]);

  const headerBadge = (
    <div
      style={{
        width: 64,
        height: 64,
        borderRadius: "50%",
        background: "linear-gradient(145deg, #ffffff 0%, #e8f1f8 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 16px",
        boxShadow: "0 8px 24px rgba(51, 122, 183, 0.2)",
      }}
    >
      <GoogleAdsLogo size={36} title="" />
    </div>
  );

  const stepsItems = [
    { title: "Sign in", description: "Google OAuth" },
    { title: "Ads account", description: "Customer ID" },
    { title: "Conversion", description: "autobot_bookings" },
  ];

  const renderProcessing = () => (
    <div style={{ textAlign: "center", padding: "32px 16px" }}>
      {headerBadge}
      <Title level={3} style={{ marginBottom: 8 }}>
        Google Ads
      </Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
        {statusLine}
      </Text>
      <Steps
        direction="vertical"
        current={stepsCurrent}
        size="small"
        items={stepsItems}
        style={{ maxWidth: 520, margin: "0 auto 24px", textAlign: "left" }}
      />
      <Spin size="large" />
      <Text type="secondary" style={{ display: "block", marginTop: 20 }}>
        Please wait — we’re connecting your account and preparing conversions.
      </Text>
    </div>
  );

  const renderChoose = () => (
    <div>
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        {headerBadge}
        <Title level={3} style={{ marginBottom: 8 }}>
          Choose Google Ads account
        </Title>
        <Text type="secondary">{statusLine}</Text>
      </div>
      <Steps
        direction="vertical"
        current={stepsCurrent}
        size="small"
        items={stepsItems}
        style={{ marginBottom: 20 }}
      />
      <Alert
        message="Multiple accounts found"
        description="Select the customer account that should receive booking conversions for this organization."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      <Radio.Group
        value={selectedCustomerId}
        onChange={(e) => setSelectedCustomerId(e.target.value)}
        style={{ width: "100%" }}
      >
        <List
          bordered
          dataSource={customers}
          style={{ borderRadius: 8, overflow: "hidden" }}
          renderItem={(c) => (
            <List.Item
              style={{
                padding: "14px 16px",
                cursor: "pointer",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(51, 122, 183, 0.06)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
            >
              <Radio value={c.customerId} style={{ width: "100%" }}>
                <div>
                  <Text strong style={{ fontSize: 15 }}>
                    {c.descriptiveName || "Google Ads customer"}
                  </Text>
                  <div>
                    <Text type="secondary">
                      Customer ID: <Text strong>{c.customerId}</Text>
                    </Text>
                  </div>
                </div>
              </Radio>
            </List.Item>
          )}
        />
      </Radio.Group>
      <Divider style={{ margin: "20px 0 16px" }} />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Button size="large" onClick={goToOrgSettings}>
          Back to settings
        </Button>
        <Button
          type="primary"
          size="large"
          loading={actionLoading}
          disabled={!selectedCustomerId}
          onClick={() => linkCustomerAndFinish(selectedCustomerId)}
        >
          Finish setup
        </Button>
      </div>
    </div>
  );

  const renderInput = () => (
    <div>
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        {headerBadge}
        <Title level={3} style={{ marginBottom: 8 }}>
          Enter customer ID
        </Title>
        <Text type="secondary">{statusLine}</Text>
      </div>
      <Steps
        direction="vertical"
        current={stepsCurrent}
        size="small"
        items={stepsItems}
        style={{ marginBottom: 20 }}
      />
      <Alert
        message="No accessible accounts from API"
        description="Paste the 10-digit customer ID from Google Ads (top right in the account). We’ll link it and create the autobot_bookings conversion."
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
      />
      <Input
        size="large"
        value={customerIdInput}
        onChange={(e) => setCustomerIdInput(e.target.value)}
        placeholder="e.g. 1234567890"
        maxLength={20}
        style={{ marginBottom: 16 }}
      />
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Button size="large" onClick={goToOrgSettings}>
          Back to settings
        </Button>
        <Button
          type="primary"
          size="large"
          loading={actionLoading}
          disabled={!customerIdInput.replace(/\D/g, "").trim()}
          onClick={() => linkCustomerAndFinish(customerIdInput)}
        >
          Finish setup
        </Button>
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div style={{ textAlign: "center", padding: "40px 16px" }}>
      <CheckCircleOutlined
        style={{ fontSize: 64, color: "#34A853", marginBottom: 16 }}
      />
      <Title level={3} style={{ color: "#34A853", marginBottom: 8 }}>
        Google Ads connected
      </Title>
      <Text type="secondary" style={{ fontSize: 15 }}>
        Conversions are configured for <Text strong>autobot_bookings</Text>. Redirecting to
        Integration Keys…
      </Text>
    </div>
  );

  const renderError = () => (
    <div style={{ textAlign: "center", padding: "32px 16px" }}>
      <ExclamationCircleOutlined
        style={{ fontSize: 64, color: "#ff4d4f", marginBottom: 16 }}
      />
      <Title level={3} style={{ color: "#ff4d4f", marginBottom: 12 }}>
        Couldn’t finish Google Ads setup
      </Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 24, maxWidth: 480, margin: "0 auto 24px" }}>
        {errorMessage}
      </Text>
      <Space>
        <Button type="primary" size="large" onClick={() => window.location.reload()}>
          Try again
        </Button>
        <Button size="large" onClick={goToOrgSettings}>
          Integration Keys
        </Button>
      </Space>
    </div>
  );

  const renderBody = () => {
    switch (flowStep) {
      case "connecting":
        return renderProcessing();
      case "choose":
        return renderChoose();
      case "input":
        return renderInput();
      case "success":
        return renderSuccess();
      case "error":
        return renderError();
      default:
        return renderProcessing();
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        /* Same family as app sidebar: Ant Design dark Sider / Menu (`siderBg` #001529, trigger #002140) */
        background: `
          radial-gradient(ellipse 100% 70% at 50% 0%, rgba(255, 255, 255, 0.08) 0%, transparent 50%),
          linear-gradient(165deg, #001529 0%, #002140 42%, #001529 100%)
        `,
      }}
    >
      <Card
        style={{
          width: "100%",
          maxWidth: 720,
          borderRadius: 16,
          background: "rgba(255, 255, 255, 0.98)",
          boxShadow:
            "0 4px 6px rgba(0, 0, 0, 0.07), 0 24px 48px rgba(0, 0, 0, 0.28), 0 0 0 1px rgba(255, 255, 255, 0.08)",
          border: "1px solid rgba(255, 255, 255, 0.22)",
        }}
        styles={{ body: { padding: flowStep === "connecting" ? 32 : 28 } }}
      >
        {renderBody()}
      </Card>
    </div>
  );
};

export default GoogleAdsOAuthCallback;
